import {
  RegistrationSoftArchivedFragment,
  RegistrationSoftArchivedFragmentDoc,
  HomeGroupFragment,
  HomeRegistrationFragment,
  HomeRegistrationFragmentDoc,
  HomeRegistrationsDocument,
  HomeRegistrationsQueryResult,
  HomeRegistrationsQueryVariables,
  SoftArchiveRegistrationMutationFn,
  SoftArchiveRegistrationMutationHookResult,
  useSoftArchiveRegistrationMutation
} from "@pathwright/graphql-client/src/generated"
import { NormalizedCacheObject, useApolloClient } from "@apollo/client"
import {
  useHomeRegistrationVars,
  useHomeSoftArchivedRegistrationVars
} from "./HomeRegistrationQueryVars"

// TODO: mv to general purpose type, including all our fragments?

// Type all fragments, included __ref keys for ref.
// TODO: need to use a generic component for mapping fragment object types
// to __refs (which won't work in all cases, but will tend to).
type FragmentCache = {
  [
    key: `${NonNullable<HomeRegistrationFragment["__typename"]>}:${number}`
  ]: HomeRegistrationFragment & {
    group: {
      __ref: `${NonNullable<HomeGroupFragment["__typename"]>}:${number}`
    }
  }
  [
    key: `${NonNullable<HomeGroupFragment["__typename"]>}:${number}`
  ]: HomeGroupFragment
}

// Define the cache shape.
type TypedCache = NormalizedCacheObject & FragmentCache

// Get the fields and values of the registration that are mutated by the mutation.
function getSyncedRegisrationData(registration: HomeRegistrationFragment) {
  const {
    softArchivedTime,
    send_discussion_notifications,
    send_due_notifications
  } = registration

  return {
    softArchivedTime,
    send_discussion_notifications,
    send_due_notifications
  }
}

// Must consider child registrations which will be automatically soft-un/archived when
// the parent is (also updates notification settings).

// Toggles the soft-archived state of a registration based on whether the registration
// is currently soft-archived or not.
function useSoftArchiveRegistration(
  registration: HomeRegistrationFragment,
  shouldUpdateCache: boolean = true
): SoftArchiveRegistrationMutationHookResult {
  const shouldArchive = !registration.softArchivedTime
  const unifiedHomeRegistrationVars = useHomeRegistrationVars()
  const unifiedHomeSoftArchivedRegistrationVars =
    useHomeSoftArchivedRegistrationVars()
  const client = useApolloClient()

  // We must extract the cache data in order to find cached child cohorts.
  let syncingChildRegistrationIds: number[] = []

  const optimisticRegistration: HomeRegistrationFragment = shouldArchive
    ? {
        ...registration,
        softArchivedTime: new Date(),
        // Archiving a registration should turn off the user's notifications for that registration.
        send_discussion_notifications: false,
        send_due_notifications: false,
        __typename: "Registration"
      }
    : {
        ...registration,
        softArchivedTime: null,
        // Archiving a registration should turn on the user's notifications for that registration.
        send_discussion_notifications: true,
        send_due_notifications: true,
        __typename: "Registration"
      }

  // Update the appropriate registration queries to either add or remove the registration
  // based on whether it is being soft archived or not.
  function updateCachedRegistration(shouldArchive: boolean) {
    let refetchQueries: string[] = []

    if (shouldUpdateCache) {
      const query = HomeRegistrationsDocument
      const queryVars = [
        unifiedHomeRegistrationVars,
        unifiedHomeSoftArchivedRegistrationVars
      ]

      for (const variables of queryVars) {
        const shouldInsert = shouldArchive
          ? variables.softArchived?.neq === null
          : variables.softArchived?.eq === null

        const data = client.readQuery<
          HomeRegistrationsQueryResult["data"],
          HomeRegistrationsQueryVariables
        >({
          query,
          variables
        })

        if (data) {
          const registrations = data!.me!.registrations!
          // List of registration IDs that should move from/to query.
          const movingRegistrationIds = [
            registration.id,
            ...syncingChildRegistrationIds
          ]

          type HomeRegistrationEdge = {
            __typename?: "RegistrationEdge"
            node: HomeRegistrationFragment
          }
          type HomeRegistrationEdges = HomeRegistrationEdge[]

          function isHomeRegistrationEdge(
            edge:
              | HomeRegistrationEdge
              | { node?: HomeRegistrationFragment | null }
              | null
              | undefined
          ): edge is HomeRegistrationEdge {
            return !!edge?.node
          }

          // Insert the registration(s).
          function insertRegistrationEdges(): HomeRegistrationEdges {
            return [
              ...(registrations?.edges?.filter(isHomeRegistrationEdge) || []),
              ...movingRegistrationIds.map((id) => ({
                node: client.readFragment({
                  fragment: HomeRegistrationFragmentDoc,
                  fragmentName: "HomeRegistration",
                  id: `Registration:${id}`,
                  variables: {
                    positioningContext: variables.positioningContext!
                  }
                })
              }))
            ].filter(isHomeRegistrationEdge)
          }

          // Remove the registration(s).
          function removeRegistrationEdges(): HomeRegistrationEdges {
            return (
              registrations?.edges
                ?.filter(isHomeRegistrationEdge)
                .filter((edge) => {
                  return !movingRegistrationIds.includes(edge.node.id)
                }) || []
            )
          }

          // If we should insert, then we should add the registration back to
          // user's home screen, otherwise, remove.
          const nextEdges = shouldInsert
            ? insertRegistrationEdges()
            : removeRegistrationEdges()

          const nextData: HomeRegistrationsQueryResult["data"] = {
            me: {
              ...data?.me!,
              registrations: {
                ...registrations,
                edges: nextEdges
              }
            }
          }

          try {
            client.writeQuery({
              query,
              variables,
              data: nextData
            })
          } catch (error) {
            // It's possible we were not provided a registration of the type HomeRegistrationFragment
            // so let's just silently ignore.
            refetchQueries = ["HomeRegistrations"]
          }
        }
      }
    } else {
      refetchQueries = ["HomeRegistrations"]
    }

    return refetchQueries
  }

  function storeSyncingChildRegistrationIds() {
    // Extract cache before operation.
    const cacheData = client.cache.extract() as TypedCache

    if (!registration.group?.parent_section_id) {
      const registrationIds = Object.entries(cacheData).reduce(
        (acc, [key, _data]) => {
          const [typename, id] = key.split(":")
          if (typename === registration.__typename!) {
            // Properly typed data!
            const childRegistration = cacheData[`${typename}:${parseInt(id)}`]
            const childGroup = cacheData[childRegistration!.group?.__ref]

            // Must be child registration that has matching softArchivedTime
            // (as that field can only sync when matching).
            if (
              childGroup?.parent_section_id === registration.group?.id &&
              childRegistration.softArchivedTime ===
                registration.softArchivedTime
            ) {
              acc.push(childRegistration.id)
            }
          }

          return acc
        },
        [] as number[]
      )

      syncingChildRegistrationIds = registrationIds
    }
  }

  // Ensure the child registrations have same settings as parent.
  // When parent registration is soft-un/archived, the same soft-archived
  // time is applied to all child registrations, so match that behavior.
  function updateCachedSyncingChildRegistrations(
    registration: HomeRegistrationFragment
  ) {
    syncingChildRegistrationIds.forEach((id) => {
      const fragment = RegistrationSoftArchivedFragmentDoc

      const key = `Registration:${id}`

      const data = client.readFragment<RegistrationSoftArchivedFragment>({
        fragment,
        fragmentName: "RegistrationSoftArchived",
        id: key
      })

      client.writeFragment({
        // Scope a Group fragment to the keys we need.
        fragment,
        fragmentName: "RegistrationSoftArchived",
        id: key,
        data: {
          ...data,
          ...getSyncedRegisrationData(registration)
        }
      })
    })
  }

  const [mutate, state] = useSoftArchiveRegistrationMutation({
    variables: {
      id: registration.id,
      softArchive: shouldArchive,
      positioningContext: unifiedHomeRegistrationVars.positioningContext!
    },
    optimisticResponse: {
      softArchiveRegistration: optimisticRegistration
    },
    onError() {
      // Restore cache when mutation fails.
      updateCachedSyncingChildRegistrations(registration)
      const refetchQueries = updateCachedRegistration(!shouldArchive)
      client.refetchQueries({ include: refetchQueries })
    }
  })

  const handleMutate: SoftArchiveRegistrationMutationFn = async (options) => {
    // Store the ids of the child registrations that "sync" soft archive setting
    // with the registration being soft un/archived. We can use this stored list
    // in all operations, both for moving them from one query to another and for
    // updating the synced fields.
    storeSyncingChildRegistrationIds()
    const refetchQueries = updateCachedRegistration(shouldArchive)
    // Update cache optimistically for child registrations (before server response.
    updateCachedSyncingChildRegistrations(optimisticRegistration)
    const result = await mutate(options)
    const updatedRegistration = result.data?.softArchiveRegistration!
    // Update cache for child registrations (after server response).
    updateCachedSyncingChildRegistrations(updatedRegistration)
    client.refetchQueries({ include: refetchQueries })
    return result
  }

  return [handleMutate, state]
}

export default useSoftArchiveRegistration
